import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import apiCall from "../app/API/apiService";
import apiEndpoints from "../app/API/apiEndpoints";
import { toasty } from "../Helper";

const difficultyLevels = ["EASY", "MEDIUM", "HARD"];
const evaluationTypes = ["RUBRIC", "CRITERIA"];
const questionTypes = [
  "MCQ",
  "SUBJECTIVE_PROBING",
  "SUBJECTIVE",
  "CODING",
  "OBJECTIVE",
];

function AddQuestionDialog({ open, onClose, questionData, isEdit }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    question: "",
    question_remarks: "",
    difficulty: "",
    evaluation_type: "",
    evaluation_rubric: "",
    probe_level: "",
    probe_objective: "",
    objective: "",
    type: "",
    tags: "",
    skills: "",
    jobcategory: "",
    jobsubcategory: "",
  });
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [image, setImage] = useState(null);

  const resetForm = () => {
    setOptions(["", "", "", ""])
    setCorrectAnswer(null)
    setImage(null)
    setFormData({
      question: "",
      question_remarks: "",
      difficulty: "",
      evaluation_type: "",
      evaluation_rubric: "",
      probe_level: "",
      probe_objective: "",
      objective: "",
      type: "",
      tags: "",
      skills: "",
      jobcategory: "",
      jobsubcategory: "",
    });
  };

  // Initialize form with question data when editing
  useEffect(() => {
    console.log("Question Data:", questionData); // For debugging
    if (questionData && isEdit) {
      if (questionData?.type == "MCQ") {
        setFormData({
          question: questionData.question || "",
          difficulty: questionData.difficulty || "",
          type: questionData.type || "",
          skills: Array.isArray(questionData.skills)
            ? questionData.skills.join(", ")
            : questionData.skills || "",
        });
        setOptions(questionData?.mcq_options)
        setCorrectAnswer(questionData?.correct_option)
      } else {
        setFormData({
          question: questionData.question || "",
          question_remarks: questionData.question_remarks || "",
          difficulty: questionData.difficulty || "",
          evaluation_type: questionData.evaluation_type || "",
          evaluation_rubric: Array.isArray(questionData.evaluation_rubric)
            ? questionData.evaluation_rubric.join("\n").replace(/,/g, "") // Remove commas
            : questionData.evaluation_rubric || "",
          probe_level: questionData.probe_level?.toString() || "",
          probe_objective: questionData.probe_objective || "",
          objective: questionData.objective || "",
          type: questionData.type || "",
          tags: Array.isArray(questionData.tags)
            ? questionData.tags.join(", ")
            : questionData.tags || "",
          skills: Array.isArray(questionData.skills)
            ? questionData.skills.join(", ")
            : questionData.skills || "",
          jobcategory: Array.isArray(questionData.jobcategory)
            ? questionData.jobcategory.join(", ")
            : questionData.jobcategory || "",
          jobsubcategory: Array.isArray(questionData.jobsubcategory)
            ? questionData.jobsubcategory.join(", ")
            : questionData.jobsubcategory || "",
        });
      }

    } else if (!isEdit) {
      // Reset form when adding new question
      setFormData({
        question: "",
        question_remarks: "",
        difficulty: "",
        evaluation_type: "",
        evaluation_rubric: "",
        probe_level: "",
        probe_objective: "",
        objective: "",
        type: "",
        tags: "",
        skills: "",
        jobcategory: "",
        jobsubcategory: "",
      });
    }
  }, [questionData, isEdit]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Function to handle option changes
  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  // Function to handle image upload
  const handleImageUpload = (event) => {
    setImage(event.target.files[0]);
  };



  const handleSubmit = async () => {
    if (formData?.type == "MCQ") {
      console.log(correctAnswer,"correctAnswer")
      // Transform string inputs to arrays where needed
      if (!formData?.difficulty) return toasty("Please select question Level", "error")
      else if (formData.skills == 0) return toasty("Skill set is required", "error")
      else if (!formData?.question) return toasty("Question is required", "error");
      else if (options?.length != 4) return toasty("All options are required", "error")
      else if (correctAnswer===null) return toasty("Correct option is required", "error")
      const payload = {
        "question": formData?.question,
        "difficulty": formData?.difficulty,
        "skills": formData.skills
          .split(",")
          .map((skill) => skill.trim())
          .filter(Boolean),
        "type": formData?.type,
        "mcq_options": options,
        "correct_option": parseInt(correctAnswer) + 1
      }
      const form = new FormData();
      form.append("question_data", JSON.stringify(payload))
      form.append("question_image", image)
      try {
        const response = await apiCall("post", apiEndpoints.QUESTIONS, form);
        if (response) {
          toasty("Question added successfully", "success");
          onClose(true);
          resetForm()
        } else {
          toasty("Failed to add question", "error");
        }
      } catch (error) {
        console.log(error, "error")
      }
    }
    else {
      try {
        setIsLoading(true);
        // Transform string inputs to arrays where needed
        const payload = {
          ...formData,
          probe_level: formData.probe_level
            ? parseInt(formData.probe_level)
            : null,
          tags: formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
          skills: formData.skills
            .split(",")
            .map((skill) => skill.trim())
            .filter(Boolean),
          jobcategory: formData.jobcategory
            .split(",")
            .map((cat) => cat.trim())
            .filter(Boolean),
          jobsubcategory: formData.jobsubcategory
            .split(",")
            .map((subCat) => subCat.trim())
            .filter(Boolean),
          evaluation_rubric: formData.evaluation_rubric
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean),
        };

        if (isEdit && questionData?.id) {
          // For edit, use PUT request
          const response = await apiCall(
            "put",
            `${apiEndpoints.QUESTIONS}/${questionData.id}`,
            payload
          );

          if (response) {
            toasty("Question updated successfully", "success");
            onClose(true);
          } else {
            toasty("Failed to update question", "error");
          }
        } else {
          // For new question, use POST request
          const response = await apiCall("post", apiEndpoints.QUESTIONS, payload);

          if (response) {
            toasty("Question added successfully", "success");
            onClose(true);
          } else {
            toasty("Failed to add question", "error");
          }
        }
      } catch (error) {
        console.error(`Error ${isEdit ? "updating" : "adding"} question:`, error);
        toasty(`Error ${isEdit ? "updating" : "adding"} question`, "error");
      } finally {
        setIsLoading(false);
      }
    }

  };

  // Common styles for all form fields
  const commonFieldStyles = {
    height: "56px", // Same height as Select components
  };

  const commonTextFieldProps = {
    sx: commonFieldStyles,
    fullWidth: true,
  };

  // Special style for multiline fields
  const multilineFieldStyles = {
    "& .MuiInputBase-root": {
      height: "auto",
      minHeight: "56px",
    },
  };

  const isFormValid = () => {
    // Basic required fields that are always needed
    const requiredFields = {
      question: formData.question.trim(),
      difficulty: formData.difficulty,
      evaluation_type: formData.evaluation_type,
      evaluation_rubric: formData.evaluation_rubric.trim(),
      type: formData.type,
      objective: formData.objective.trim(),
      tags: formData.tags.trim(),
      skills: formData.skills.trim(),
      jobcategory: formData.jobcategory.trim(),
      jobsubcategory: formData.jobsubcategory.trim(),
    };

    // Additional validation for SUBJECTIVE_PROBING type
    if (formData.type === "SUBJECTIVE_PROBING") {
      if (!formData.probe_level || !formData.probe_objective.trim()) {
        return false;
      }
    }

    // Check if any required field is empty
    return Object.values(requiredFields).every((field) => field !== "");
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Question" : "Add New Question"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <FormControl fullWidth sx={commonFieldStyles}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
              label="Type"
            >
              {questionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={commonFieldStyles}>
            <InputLabel>Difficulty Level</InputLabel>
            <Select
              value={formData.difficulty}
              onChange={(e) => handleChange("difficulty", e.target.value)}
              label="Difficulty Level"
            >
              {difficultyLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            {...commonTextFieldProps}
            label="Skills (Comma separated)"
            value={formData.skills}
            onChange={(e) => handleChange("skills", e.target.value)}
            placeholder="e.g., Critical Thinking, Problem Solving"
          />
          <TextField
            {...commonTextFieldProps}
            sx={multilineFieldStyles}
            label="Question"
            multiline
            rows={1}
            value={formData.question}
            onChange={(e) => handleChange("question", e.target.value)}
          />

          {formData.type !== "MCQ" && (
            <>
              <TextField
                {...commonTextFieldProps}
                sx={multilineFieldStyles}
                label="Question Remarks"
                multiline
                rows={1}
                value={formData.question_remarks}
                onChange={(e) =>
                  handleChange("question_remarks", e.target.value)
                }
              />

              <FormControl fullWidth sx={commonFieldStyles}>
                <InputLabel>Evaluation Type</InputLabel>
                <Select
                  value={formData.evaluation_type}
                  onChange={(e) =>
                    handleChange("evaluation_type", e.target.value)
                  }
                  label="Evaluation Type"
                >
                  {evaluationTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                {...commonTextFieldProps}
                sx={multilineFieldStyles}
                label="Evaluation Rubric"
                multiline={formData.evaluation_type === "RUBRIC"}
                rows={formData.evaluation_type === "RUBRIC" ? 3 : 1}
                value={formData.evaluation_rubric}
                onChange={(e) =>
                  handleChange("evaluation_rubric", e.target.value)
                }
                placeholder={
                  formData.evaluation_type === "RUBRIC"
                    ? "Enter each rubric on a new line"
                    : "Enter criteria"
                }
                helperText={
                  formData.evaluation_type === "RUBRIC"
                    ? "Provide answers from best to worst."
                    : ""
                }
              />
            </>
          )}
          {formData.type !== "MCQ" && (
            <>
              {formData.type === "SUBJECTIVE_PROBING" && (
                <>
                  <TextField
                    {...commonTextFieldProps}
                    label="Probe Level"
                    type="number"
                    value={formData.probe_level}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d+$/.test(value)) {
                        handleChange("probe_level", value);
                      }
                    }}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    {...commonTextFieldProps}
                    sx={multilineFieldStyles}
                    label="Probe Objective"
                    multiline
                    rows={2}
                    value={formData.probe_objective}
                    onChange={(e) =>
                      handleChange("probe_objective", e.target.value)
                    }
                  />
                </>
              )}

              <TextField
                {...commonTextFieldProps}
                sx={multilineFieldStyles}
                label="Objective"
                multiline
                rows={1}
                value={formData.objective}
                onChange={(e) => handleChange("objective", e.target.value)}
              />

              <TextField
                {...commonTextFieldProps}
                label="Tags (Comma separated)"
                value={formData.tags}
                onChange={(e) => handleChange("tags", e.target.value)}
                placeholder="e.g., OOP, Programming, Java"
              />

              <TextField
                {...commonTextFieldProps}
                label="Job Categories (Comma separated)"
                value={formData.jobcategory}
                onChange={(e) => handleChange("jobcategory", e.target.value)}
                placeholder="e.g., Software Development, Engineering"
              />

              <TextField
                {...commonTextFieldProps}
                label="Job Sub Categories (Comma separated)"
                value={formData.jobsubcategory}
                onChange={(e) => handleChange("jobsubcategory", e.target.value)}
                placeholder="e.g., Backend Development, Frontend Development"
              />
            </>
          )}
          {formData.type === "MCQ" && (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
              {options.map((option, index) => (
                <TextField
                  key={index}
                  {...commonTextFieldProps}
                  label={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Enter option ${index + 1}`}
                />
              ))}
              <FormControl fullWidth sx={commonFieldStyles}>
                <InputLabel>Correct Answer</InputLabel>
                <Select
                  value={correctAnswer}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                  label="Correct Answer"
                >
                  {options.map((option, index) => (
                    <MenuItem key={index} value={index}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose(false);
            resetForm();
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={formData?.type == "MCQ" ? null : isLoading || !isFormValid()}
          sx={{
            backgroundColor: "#2c5da7",
            "&:hover": {
              backgroundColor: "#1e4c8f",
            },
          }}
        >
          {isLoading
            ? isEdit
              ? "Updating..."
              : "Adding..."
            : isEdit
              ? "Update Question"
              : "Add Question"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddQuestionDialog;
